import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Gift,
  Mail,
  Scale,
  Search,
  Sparkles,
  Trophy,
  Info,
} from "lucide-react";
import SEO from "@/components/SEO";
import BrandIcon from "@/components/BrandIcon";
import { api } from "@/lib/api";
import { BRAND } from "@/brand";

const QUICK_AMOUNTS = [50, 100, 200, 500, 1000];

export default function RatesPage() {
  const [brands, setBrands] = useState([]);
  const [cfg, setCfg] = useState({ welcome_bonus_amount: 5, min_per_card: 50 });
  const [brandId, setBrandId] = useState("");
  const [compareIds, setCompareIds] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [balance, setBalance] = useState(100);
  const [hasReceipt, setHasReceipt] = useState(true);
  const [partial, setPartial] = useState(false);
  const [firstTime, setFirstTime] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/brands?limit=200").then((r) => {
      setBrands(r.data || []);
      if ((r.data || []).length && !brandId) {
        const amazon = (r.data || []).find((b) => b.slug === "amazon");
        setBrandId((amazon || r.data[0]).id);
      }
    });
    api.get("/config").then((r) => setCfg(r.data || {}));
    // eslint-disable-next-line
  }, []);

  const computeFor = (brand) => {
    if (!brand) return { rate: 0, basis: "no_receipt", basePayout: 0, bonus: 0, total: 0 };
    const rates = brand.rates || { receipt: brand.rate, no_receipt: brand.rate, partial: brand.rate };
    let r;
    if (partial) r = rates.partial;
    else if (hasReceipt) r = rates.receipt;
    else r = rates.no_receipt;
    const bp = Math.round(balance * (r / 100) * 100) / 100;
    const bn = firstTime ? cfg.welcome_bonus_amount || 5 : 0;
    return { rate: r, basePayout: bp, bonus: bn, total: bp + bn };
  };

  const selected = useMemo(() => brands.find((b) => b.id === brandId), [brands, brandId]);
  const { rate, basePayout, bonus, total } = useMemo(
    () => computeFor(selected),
    // eslint-disable-next-line
    [selected, balance, hasReceipt, partial, firstTime, cfg]
  );

  const comparisonRows = useMemo(() => {
    if (!compareMode) return [];
    const allIds = [brandId, ...compareIds].filter(Boolean);
    return allIds.map((id) => {
      const b = brands.find((x) => x.id === id);
      return { brand: b, calc: computeFor(b) };
    });
    // eslint-disable-next-line
  }, [compareMode, compareIds, brandId, brands, balance, hasReceipt, partial, firstTime, cfg]);

  const bestRow = useMemo(() => {
    if (comparisonRows.length < 2) return null;
    return comparisonRows.reduce((best, r) => (!best || r.calc.total > best.calc.total ? r : best), null);
  }, [comparisonRows]);

  const toggleCompare = () => {
    setCompareMode((v) => {
      const next = !v;
      if (next && compareIds.length === 0) {
        const visa = brands.find((b) => b.slug === "visa" && b.id !== brandId);
        const alt = visa || brands.find((b) => b.id !== brandId);
        if (alt) setCompareIds([alt.id]);
      }
      return next;
    });
  };

  const addCompareSlot = () => {
    if (compareIds.length >= 2) return;
    const used = new Set([brandId, ...compareIds]);
    const next = brands.find((b) => !used.has(b.id));
    if (next) setCompareIds([...compareIds, next.id]);
  };

  const setCompareSlot = (idx, id) => setCompareIds((arr) => arr.map((v, i) => (i === idx ? id : v)));
  const removeCompareSlot = (idx) => setCompareIds((arr) => arr.filter((_, i) => i !== idx));

  const goSubmit = (brand) => {
    const b = brand || selected;
    if (!b) return;
    nav(`/submit?brand=${encodeURIComponent(b.slug)}&balance=${balance}`);
  };

  return (
    <>
      <SEO
        title="Gift card rate calculator"
        description="Live gift-card rate calculator. Estimate your payout before you submit — check current exchange rates across 70+ brands side-by-side."
        path="/rates"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Rates", path: "/rates" }]}
      />

      <section className="search-home-hero-section" data-testid="rates-hero" style={{ paddingBottom: 30 }}>
        <div className="main-container w-container">
          <div className="search-heading" style={{ textAlign: "center", maxWidth: 780, margin: "0 auto" }}>
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
              Rate calculator
            </div>
            <h1 className="h1 _700-bold">See your exact payout — <span style={{ color: "#346af8" }}>before you sell</span></h1>
            <p className="main-body-text _500-medium" style={{ marginTop: 18, color: "#4a4d5f", maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
              Pick a brand, set the balance, and watch the number update live. The quote you see is locked at submission.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20 md:pb-28" style={{ position: "relative", zIndex: 3 }}>
        <div className="grid lg:grid-cols-12 gap-6 md:gap-8 items-start">
          {/* ============ INPUTS ============ */}
          <div className="lg:col-span-7 tt-card p-6 md:p-9">
            {/* STEP 01 — Brand */}
            <div className="tt-step-eyebrow">Step 01 — Pick your brand</div>
            <div className="mt-4">
              <BrandSelect brands={brands} selectedId={brandId} excludeIds={compareIds} onSelect={(id) => setBrandId(id)} />
            </div>

            {/* Compare toggle */}
            <button
              type="button"
              onClick={toggleCompare}
              data-testid="rates-compare-toggle"
              className={`mt-4 w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all text-left ${
                compareMode ? "bg-[#f4f6ff] border-[#346af8]" : "bg-white border-[#e4e8f7] hover:border-[#b9c8f5]"
              }`}
            >
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${compareMode ? "bg-[#346af8] text-white" : "bg-[#f4f6ff] text-[#848991]"}`}>
                <Scale className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0b0d17] text-sm md:text-base" style={{ fontFamily: "Satoshi, sans-serif" }}>Compare brands</div>
                <div className="text-xs text-[#848991] mt-0.5">Side-by-side payout for up to 3 cards at the same balance</div>
              </div>
              <span className={`relative w-11 h-6 rounded-full transition-all flex-shrink-0 ${compareMode ? "bg-[#346af8]" : "bg-[#e4e8f7]"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${compareMode ? "left-[22px]" : "left-0.5"}`} />
              </span>
            </button>

            {compareMode && (
              <div className="mt-4 space-y-3">
                {compareIds.map((cid, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#848991] font-bold">Brand {i + 2}</span>
                      <button type="button" onClick={() => removeCompareSlot(i)} data-testid={`rates-compare-remove-${i}`} className="text-xs text-[#848991] hover:text-rose-600 font-semibold">
                        Remove
                      </button>
                    </div>
                    <BrandSelect
                      brands={brands}
                      selectedId={cid}
                      excludeIds={[brandId, ...compareIds.filter((_, j) => j !== i)]}
                      onSelect={(id) => setCompareSlot(i, id)}
                    />
                  </div>
                ))}
                {compareIds.length < 2 && (
                  <button
                    type="button"
                    onClick={addCompareSlot}
                    data-testid="rates-compare-add"
                    className="w-full h-12 rounded-xl border-2 border-dashed border-[#346af8]/40 text-[#346af8] font-semibold hover:bg-[#f4f6ff] hover:border-[#346af8] transition-all text-sm"
                  >
                    + Add another brand to compare
                  </button>
                )}
              </div>
            )}

            {/* STEP 02 — Balance */}
            <div className="tt-step-eyebrow mt-9">Step 02 — Card balance</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {QUICK_AMOUNTS.map((a) => (
                <button key={a} type="button" onClick={() => setBalance(a)} data-testid={`rates-quick-${a}`} className={`tt-amount-chip ${balance === a ? "on" : ""}`}>
                  ${a}
                </button>
              ))}
            </div>
            <div className="relative mt-3">
              <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[#848991] text-xl font-semibold">$</span>
              <input
                type="number"
                inputMode="decimal"
                value={balance === 0 ? "" : balance}
                onChange={(e) => setBalance(Number(e.target.value || 0))}
                min={selected?.min ?? 50}
                max={selected?.max ?? 2000}
                step="1"
                placeholder="Enter card value"
                data-testid="rates-balance"
                className="tt-inp"
                style={{ height: 64, paddingLeft: 40, fontSize: 26, fontWeight: 700, fontFamily: "Satoshi, sans-serif" }}
              />
            </div>
            {selected && (
              <div className="mt-2 text-xs text-[#848991]">
                Accepted range: ${selected.min}–${selected.max}
              </div>
            )}

            {/* STEP 03 — Details */}
            <div className="tt-step-eyebrow mt-9">Step 03 — Card details</div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-bold text-[#5b6270] mb-2 uppercase tracking-wider">Receipt?</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" className={`tt-pill ${hasReceipt ? "on" : ""}`} onClick={() => setHasReceipt(true)} data-testid="rates-receipt-yes">
                    {hasReceipt && <Check className="w-4 h-4" />} Yes
                  </button>
                  <button type="button" className={`tt-pill ${!hasReceipt ? "on" : ""}`} onClick={() => setHasReceipt(false)} data-testid="rates-receipt-no">
                    {!hasReceipt && <Check className="w-4 h-4" />} No
                  </button>
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#5b6270] mb-2 uppercase tracking-wider">Balance status</div>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" className={`tt-pill ${!partial ? "on" : ""}`} onClick={() => setPartial(false)} data-testid="rates-balance-full">
                    {!partial && <Check className="w-4 h-4" />} Full
                  </button>
                  <button type="button" className={`tt-pill ${partial ? "on" : ""}`} onClick={() => setPartial(true)} data-testid="rates-balance-partial">
                    {partial && <Check className="w-4 h-4" />} Partial
                  </button>
                </div>
              </div>
            </div>

            {/* First-time bonus — HIDDEN until promo is active */}
            {false && (
            <button
              type="button"
              onClick={() => setFirstTime((v) => !v)}
              data-testid="rates-firsttime-toggle"
              className={`mt-5 w-full flex items-center gap-4 px-5 py-4 rounded-xl border transition-all text-left ${
                firstTime ? "bg-[#f4f6ff] border-[#346af8]" : "bg-white border-[#e4e8f7] hover:border-[#b9c8f5]"
              }`}
            >
              <span className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${firstTime ? "bg-[#346af8] text-white" : "bg-[#f4f6ff] text-[#848991]"}`}>
                <Sparkles className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0b0d17]" style={{ fontFamily: "Satoshi, sans-serif" }}>First-time customer</div>
                <div className={`text-sm mt-0.5 ${firstTime ? "text-[#346af8] font-semibold" : "text-[#848991]"}`}>
                  +${cfg.welcome_bonus_amount || 5} welcome bonus
                </div>
              </div>
              <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${firstTime ? "bg-[#346af8] border-[#346af8]" : "border-[#e4e8f7]"}`}>
                {firstTime && <Check className="w-3.5 h-3.5 text-white" />}
              </span>
            </button>
            )}

            {/* Unlisted brand */}
            <div className="mt-5 flex items-start gap-3 px-4 py-3.5 rounded-xl bg-[#f4f6ff] border border-[#e4e8f7]">
              <span className="w-9 h-9 rounded-lg bg-white text-[#346af8] flex items-center justify-center flex-shrink-0 border border-[#e4e8f7]">
                <Mail className="w-4 h-4" />
              </span>
              <div className="text-sm text-[#313233] leading-relaxed">
                <strong className="text-[#0b0d17]">Don&apos;t see your card listed?</strong>{" "}
                Email{" "}
                <a href={`mailto:${BRAND.supportEmail}?subject=Unlisted%20brand%20quote`} className="text-[#346af8] font-semibold hover:underline" data-testid="rates-support-email">
                  {BRAND.supportEmail}
                </a>{" "}
                and we&apos;ll send you a quote.
              </div>
            </div>
          </div>

          {/* ============ PAYOUT PANEL ============ */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28">
            {compareMode ? (
              <ComparePanel rows={comparisonRows} bestRow={bestRow} balance={balance} hasReceipt={hasReceipt} partial={partial} firstTime={firstTime} onTrade={goSubmit} />
            ) : (
              <SinglePanel selected={selected} rate={rate} basePayout={basePayout} bonus={bonus} total={total} balance={balance} hasReceipt={hasReceipt} partial={partial} firstTime={firstTime} onTrade={() => goSubmit()} />
            )}

            <div data-testid="rates-market-disclaimer" className="mt-4 flex items-start gap-2.5 px-4 py-3 rounded-xl bg-white border border-[#e4e8f7]">
              <Info className="w-3.5 h-3.5 text-[#848991] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#848991] leading-relaxed">
                Rates shown are indicative and may change with market demand, inventory and brand-specific fraud risk. The final payout is locked at submission once we verify your card balance.
                <span data-testid="rates-trademark-disclaimer" className="block mt-2">
                  Brand names and logos are trademarks of their respective owners. Their appearance on this website does not imply sponsorship, endorsement, or partnership with GC SPARK.
                </span>
              </p>
            </div>
          </aside>
        </div>

        <TierReference />
      </section>
    </>
  );
}

/* ============================== SINGLE PAYOUT PANEL ============================== */
function SinglePanel({ selected, rate, basePayout, bonus, total, balance, hasReceipt, partial, firstTime, onTrade }) {
  return (
    <div className="tt-panel" data-testid="rates-payout-panel">
      <div className="tt-panel-inner">
        <div className="tt-panel-eyebrow">Your payout</div>
        <AnimatePresence mode="wait">
          <motion.div
            key={total.toFixed(2)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="tt-panel-total"
            data-testid="rates-total"
          >
            ${total.toFixed(2)}
          </motion.div>
        </AnimatePresence>
        {selected && (
          <div className="mt-3 text-sm">
            Selling <strong style={{ color: "#fff" }}>{selected.name}</strong> · ${Number(balance || 0).toFixed(2)}
          </div>
        )}
        <div className="mt-6">
          <div className="tt-panel-line">
            <span>Rate</span>
            <span className="v">{rate}%</span>
          </div>
          <div className="tt-panel-line">
            <span>Base payout</span>
            <span className="v">${basePayout.toFixed(2)}</span>
          </div>
          {bonus > 0 && (
            <div className="tt-panel-line hi">
              <span className="inline-flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> Welcome bonus</span>
              <span className="v">+${bonus.toFixed(2)}</span>
            </div>
          )}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <span className="tt-chip">{hasReceipt ? "With receipt" : "No receipt"}</span>
          <span className="tt-chip">{partial ? "Partially used" : "Full balance"}</span>
          {firstTime && <span className="tt-chip">First-time customer</span>}
        </div>
        <button type="button" onClick={onTrade} data-testid="rates-trade-cta" className="tt-panel-cta">
          Submit &amp; Trade Cards <ArrowRight className="w-5 h-5" />
        </button>
        <div className="mt-4 text-center text-xs" style={{ color: "#848da8" }}>
          No fees · Typically paid within 24 hours · Reviewed by a person
        </div>
      </div>
    </div>
  );
}

/* ============================== COMPARE PAYOUT PANEL ============================== */
function ComparePanel({ rows, bestRow, balance, hasReceipt, partial, firstTime, onTrade }) {
  const validRows = rows.filter((r) => r.brand);
  return (
    <div className="tt-panel" data-testid="rates-payout-panel">
      <div className="tt-panel-inner">
        <div className="tt-panel-eyebrow">Side-by-side</div>
        {bestRow && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4" style={{ background: "rgba(52,106,248,.2)", color: "#8fadff" }}>
            <Trophy className="w-3.5 h-3.5" />
            Best: {bestRow.brand.name} · ${bestRow.calc.total.toFixed(2)}
          </div>
        )}

        <div className="space-y-3" data-testid="rates-compare-rows">
          {validRows.map(({ brand, calc }) => {
            const isBest = bestRow && brand.id === bestRow.brand.id;
            return (
              <div key={brand.id} data-testid={`rates-compare-row-${brand.slug}`} className={`tt-panel-row ${isBest ? "best" : ""}`}>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg flex-shrink-0 overflow-hidden" style={{ background: brand.color }}>
                    <BrandIcon logoKey={brand.logo_key} name={brand.name} className="w-full h-full" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate" style={{ fontFamily: "Satoshi, sans-serif" }}>{brand.name}</div>
                    <div className="text-xs" style={{ color: "#848da8" }}>{calc.rate}% rate</div>
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={calc.total.toFixed(2)}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.2 }}
                      className="font-bold text-lg"
                      style={{ fontFamily: "Satoshi, sans-serif", color: isBest ? "#8fadff" : "#fff" }}
                    >
                      ${calc.total.toFixed(2)}
                    </motion.div>
                  </AnimatePresence>
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-2 text-xs" style={{ color: "#848da8" }}>
                  <span>
                    ${balance} × {calc.rate}% = ${calc.basePayout.toFixed(2)}
                    {calc.bonus > 0 && <span style={{ color: "#9db9ff", marginLeft: 6 }}>+${calc.bonus.toFixed(2)} bonus</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => onTrade(brand)}
                    data-testid={`rates-compare-trade-${brand.slug}`}
                    className="inline-flex items-center gap-1 font-bold hover:underline"
                    style={{ color: "#8fadff", background: "none", border: "none", cursor: "pointer" }}
                  >
                    Sell this <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <span className="tt-chip">{hasReceipt ? "With receipt" : "No receipt"}</span>
          <span className="tt-chip">{partial ? "Partially used" : "Full balance"}</span>
          {firstTime && <span className="tt-chip">First-time customer</span>}
        </div>

        {bestRow && (
          <button type="button" onClick={() => onTrade(bestRow.brand)} data-testid="rates-compare-trade-best" className="tt-panel-cta">
            Sell {bestRow.brand.name} — best payout <ArrowRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ============================== BRAND SELECT ============================== */
function BrandSelect({ brands, selectedId, onSelect, excludeIds = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const selected = brands.find((b) => b.id === selectedId);
  const excluded = new Set(excludeIds);

  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = brands.filter((b) => !excluded.has(b.id));
    return s
      ? base.filter((b) => b.name.toLowerCase().includes(s))
      : base.slice().sort((a, b) => (b.rates?.receipt ?? b.rate) - (a.rates?.receipt ?? a.rate));
    // eslint-disable-next-line
  }, [brands, q, excludeIds.join(",")]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="rates-brand-trigger"
        className="w-full flex items-center gap-3 h-16 px-5 rounded-xl border border-[#e4e8f7] bg-white hover:border-[#346af8] transition-all"
      >
        {selected ? (
          <>
            <span className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden" style={{ background: selected.color }}>
              <BrandIcon logoKey={selected.logo_key} name={selected.name} className="w-full h-full" />
            </span>
            <div className="flex-1 text-left min-w-0">
              <div className="font-semibold text-[#0b0d17] text-base truncate" style={{ fontFamily: "Satoshi, sans-serif" }}>{selected.name}</div>
              <div className="text-[11px] text-[#848991]">
                {selected.category} · Up to <span className="text-[#346af8] font-bold">{selected.rates?.receipt ?? selected.rate}%</span>
              </div>
            </div>
          </>
        ) : (
          <span className="text-[#848991]">Choose a gift card brand</span>
        )}
        <ChevronDown className={`w-5 h-5 text-[#848991] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            className="absolute z-30 left-0 right-0 mt-2 bg-white border border-[#e4e8f7] rounded-xl shadow-2xl overflow-hidden"
          >
            <div className="p-3 border-b border-[#e4e8f7] sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#848991]" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search 70 brands"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#e4e8f7] text-sm focus:outline-none focus:border-[#346af8]"
                  data-testid="rates-brand-search"
                />
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {filtered.map((b) => (
                <button
                  type="button"
                  key={b.id}
                  onClick={() => { onSelect(b.id); setOpen(false); setQ(""); }}
                  data-testid={`rates-brand-option-${b.slug}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[#f4f6ff] ${b.id === selectedId ? "bg-[#f4f6ff]" : ""}`}
                >
                  <span className="w-9 h-9 rounded-lg flex-shrink-0 overflow-hidden" style={{ background: b.color }}>
                    <BrandIcon logoKey={b.logo_key} name={b.name} className="w-full h-full" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0b0d17] truncate">{b.name}</div>
                    <div className="text-[11px] text-[#848991]">{b.category}</div>
                  </div>
                  <span className="text-sm text-[#346af8] font-bold">{b.rates?.receipt ?? b.rate}%</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-[#848991]">No brands match &quot;{q}&quot;</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ============================== TIER REFERENCE ============================== */
function TierReference() {
  const TIERS = [
    { n: 1, ex: "Amazon · Adidas · Best Buy", rates: "92 / 78 / 60" },
    { n: 2, ex: "Apple · Visa · Roblox · eBay", rates: "92 / 78 / 50" },
    { n: 3, ex: "Target · Walmart · Steam · Nike", rates: "92 / 78 / 55" },
    { n: 4, ex: "Airbnb", rates: "85 / 74 / 55" },
    { n: 5, ex: "American Express", rates: "92 / 75 / 55" },
    { n: 6, ex: "Netflix · Microsoft · Uber · Xbox", rates: "85 / 70 / 45" },
    { n: 7, ex: "Ray-Ban · Sunglass Hut · Sierra", rates: "80 / 65 / 40" },
  ];
  return (
    <div className="mt-16 md:mt-24">
      <div className="text-center max-w-2xl mx-auto">
        <span className="tt-step-eyebrow" style={{ justifyContent: "center" }}>How rates work</span>
        <h2 className="mt-4 font-bold text-[#0b0d17]" style={{ fontFamily: "Satoshi, sans-serif", fontSize: "clamp(28px,3.6vw,40px)", letterSpacing: "-0.025em" }}>
          Seven published tiers
        </h2>
        <p className="mt-3 text-[#5b6270] leading-relaxed">
          Every brand belongs to one tier. Rates show as <strong>with-receipt / standard / partial</strong>. No surprises — what you see is what you&apos;ll receive on a verified balance.
        </p>
      </div>
      <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TIERS.map((t) => (
          <div key={t.n} data-testid={`rates-tier-${t.n}`} className="tt-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-[#848991] font-bold">Tier {t.n}</span>
              <span className="text-sm text-[#346af8] font-bold">{t.rates}%</span>
            </div>
            <div className="mt-3 text-sm text-[#0b0d17] leading-relaxed">{t.ex}</div>
          </div>
        ))}
        <Link
          to="/accepted-cards"
          data-testid="rates-view-all-cards"
          className="rounded-2xl p-5 flex flex-col justify-between transition-all group"
          style={{ background: "#0b0d17", color: "#fff", textDecoration: "none" }}
        >
          <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#848da8" }}>70 brands · 8 categories</span>
          <span className="font-semibold text-base mt-3 inline-flex items-center gap-1.5" style={{ fontFamily: "Satoshi, sans-serif" }}>
            See the full list
            <ArrowUpRight className="w-4 h-4 group-hover:rotate-12 transition-transform" style={{ color: "#8fadff" }} />
          </span>
        </Link>
      </div>
    </div>
  );
}
